// src/components/AdminAssessmentSchedule.jsx
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import DynamicModal from "./DynamicModal";
import EditAssessmentScheduleModal from "./EditAssessmentScheduleModal";
import {
  getAssessmentSchedules,
  updateAssessmentSchedule,
  deleteAssessmentSchedule,
} from "../redux/slices/assessmentScheduleSlice";
import { getMyFacilities } from "../redux/slices/facilitySlice";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";

const AdminAssessmentSchedule = forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const {
    schedules = [],
    isLoading,
    error,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.assessmentSchedule || {});

  const {
    myFacilities = { data: [] },
    isLoading: isLoadingFacilities = false,
  } = useSelector((state) => state.facility || {});

  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState("all");

  const { dateFilter, updateFilter, getDateRange } = useDateFilter({
    viewType: "all", // Override to show all time by default
  });

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const buttonRefs = useRef([]);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // Expose getFilterParams method to parent component
  useImperativeHandle(ref, () => ({
    getFilterParams: () => {
      const dateRange = getDateRange();
      const params = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      };

      // Only add facility_id if it's not "all"
      if (selectedFacilityId !== "all") {
        params.facility_id = selectedFacilityId;
      }

      return params;
    },
  }));

  useEffect(() => {
    dispatch(getMyFacilities(1));
  }, [dispatch]);

  useEffect(() => {
    if (myFacilities.data && myFacilities.data.length > 0) {
      const facilityList = myFacilities.data.map((facility) => ({
        id: facility.id,
        name: facility.facility_name,
      }));
      setFacilities(facilityList);
    }
  }, [myFacilities]);

  const showToast = (message, type = "info") => {
    setToast({
      show: true,
      message,
      type,
    });
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  useEffect(() => {
    const dateRange = getDateRange();

    const params = {
      from_date: dateRange.from_date,
      to_date: dateRange.to_date,
    };

    dispatch(getAssessmentSchedules(params));
  }, [
    dispatch,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
  ]);

  const handleFacilityFilter = (facilityId) => {
    setSelectedFacilityId(facilityId);
  };

  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();

    if (buttonRefs.current[index]) {
      const rect = buttonRefs.current[index].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 120,
      });
    }

    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleViewDetails = (rowData) => {
    setSelectedSchedule(rowData);
    setShowViewModal(true);
    setDropdownOpen(null);
  };

  const handleEdit = (rowData) => {
    setSelectedSchedule(rowData);
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedSchedule(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedSchedule(null);
  };

  const handleDelete = (rowData) => {
    if (
      window.confirm(
        `Are you sure you want to delete schedule "${rowData.name}"?`,
      )
    ) {
      dispatch(deleteAssessmentSchedule(rowData.id)).then((result) => {
        if (result.payload?.success) {
          showToast("Assessment schedule deleted successfully!", "success");
          const dateRange = getDateRange();

          const params = {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          };

          dispatch(getAssessmentSchedules(params));
        } else {
          showToast(
            result.payload?.message || "Failed to delete schedule",
            "error",
          );
        }
      });
    }
    setDropdownOpen(null);
  };

  const handleSaveSchedule = (apiData) => {
    setIsSaving(true);

    dispatch(updateAssessmentSchedule(apiData))
      .then((action) => {
        setIsSaving(false);

        if (action.payload?.success) {
          showToast("Assessment schedule updated successfully!", "success");
          handleCloseEditModal();
          handleCloseViewModal();

          const dateRange = getDateRange();
          const params = {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          };

          dispatch(getAssessmentSchedules(params));
        } else {
          showToast(
            action.payload?.message || "Failed to update assessment schedule",
            "error",
          );
        }
      })
      .catch((error) => {
        setIsSaving(false);
        showToast("An error occurred while saving", "error");
      });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      day: { bg: "#E3F2FD", color: "#1976D2" },
      week: { bg: "#E8F5E8", color: "#2E7D32" },
      month: { bg: "#FFF3E0", color: "#F57C00" },
      year: { bg: "#F3E5F5", color: "#7B1FA2" },
    };
    const defaultColor = { bg: "#EEEEEE", color: "#616161" };
    const { bg, color } = colors[type] || defaultColor;

    return (
      <span
        style={{
          backgroundColor: bg,
          color: color,
          padding: "4px 8px",
          borderRadius: "12px",
          fontSize: "0.85rem",
          fontWeight: 500,
          textTransform: "capitalize",
        }}>
        {type}
      </span>
    );
  };

  const transformScheduleData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((schedule) => ({
      id: schedule.id,
      name: schedule.name || "N/A",
      category: schedule.category_name || "N/A",
      category_id: schedule.category_id,
      user: schedule.user_name || `User ${schedule.user_id}`,
      user_id: schedule.user_id,
      facility: schedule.facility_name || "N/A",
      facility_id: schedule.facility_id,
      facility_address: schedule.facility_address || "",
      type: schedule.type || "N/A",
      offset: schedule.offset || 0,
      lastAssigned: formatDate(schedule.last_assigned_at),
      createdAt: formatDate(schedule.created_at),
      originalData: schedule,
    }));
  };

  const scheduleData = isLoading ? [] : transformScheduleData(schedules);

  const filteredData =
    selectedFacilityId === "all"
      ? scheduleData
      : scheduleData.filter(
          (schedule) =>
            schedule.facility_id === parseInt(selectedFacilityId, 10),
        );

  const facilityOptions = [
    { id: "all", name: "All Facilities" },
    ...facilities.map((f) => ({ id: f.id, name: f.name })),
  ];

  const columns = [
    {
      name: "name",
      label: "Schedule Name",
      options: {
        customBodyRender: (value) => {
          return (
            <div style={{ fontWeight: "500", color: "#1A1A1A" }}>{value}</div>
          );
        },
      },
    },
    {
      name: "category",
      label: "Category",
    },
    {
      name: "user",
      label: "Assigned User",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = filteredData[tableMeta.rowIndex];
          return (
            <div>
              <div style={{ fontWeight: "500", color: "#1A1A1A" }}>{value}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                ID: {rowData.user_id}
              </div>
            </div>
          );
        },
      },
    },
    {
      name: "facility",
      label: "Facility",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = filteredData[tableMeta.rowIndex];
          return (
            <div>
              <div
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#E5F4FF",
                  color: "#1976D2",
                  borderRadius: "12px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  display: "inline-block",
                }}>
                {value}
              </div>
              {rowData.facility_address && (
                <div
                  style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                  {rowData.facility_address}
                </div>
              )}
            </div>
          );
        },
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        customBodyRender: (value) => {
          return getTypeBadge(value);
        },
      },
    },
    {
      name: "offset",
      label: "Offset",
      options: {
        customBodyRender: (value) => {
          return <div style={{ fontWeight: "500" }}>{value}</div>;
        },
      },
    },
    {
      name: "lastAssigned",
      label: "Last Assigned",
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                ref={(el) => (buttonRefs.current[dataIndex] = el)}
                onClick={(e) => handleDropdownToggle(dataIndex, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                }}>
                <Icon icon="mdi:dots-horizontal" width="25" height="25" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    searchPlaceholder: "Search by schedule name, category, user, facility...",
    pagination: false,
    tableBodyHeight: "auto",
    textLabels: {
      body: {
        noMatch: isLoading
          ? "Loading..."
          : "No assessment schedules found for selected period",
      },
    },
  };

  const ViewScheduleModal = ({ schedule, onClose, onEdit, onDelete }) => {
    if (!schedule) return null;

    return (
      <div>
        <div
          style={{
            background: "#F7F8FA",
            padding: "20px",
            borderRadius: "16px",
            marginBottom: "20px",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}>
          <div style={{ marginBottom: "15px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Schedule Name:</strong> {schedule.name}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Category:</strong> {schedule.category}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Assigned User:</strong> {schedule.user}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>User ID:</strong> {schedule.user_id}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Facility:</strong> {schedule.facility}
            </p>
            {schedule.facility_address && (
              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  margin: "5px 0",
                }}>
                <strong>Facility Address:</strong> {schedule.facility_address}
              </p>
            )}
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Type:</strong>{" "}
              <span style={{ textTransform: "capitalize" }}>
                {schedule.type}
              </span>
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Offset:</strong> {schedule.offset}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Last Assigned:</strong> {schedule.lastAssigned}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Created At:</strong> {schedule.createdAt}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mb-4 pb-2 border-bottom">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          {/* <div style={{ minWidth: "300px" }}>
            <DateFilter
              {...dateFilter}
              onFilterChange={updateFilter}
              size="sm"
            />
          </div> */}

          <div style={{ minWidth: "200px" }}>
            <select
              className="form-select form-select-sm"
              value={selectedFacilityId}
              onChange={(e) => handleFacilityFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "white",
                fontSize: "14px",
              }}>
              {facilityOptions.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-light text-dark p-2">
            <i className="fas fa-calendar-alt me-1"></i>
            {getDateRange().label}
          </span>

          {selectedFacilityId !== "all" && (
            <span className="badge bg-info text-white p-2">
              <i className="fas fa-building me-1"></i>
              Facility:{" "}
              {
                facilityOptions.find(
                  (f) => f.id === parseInt(selectedFacilityId, 10),
                )?.name
              }
              <button
                onClick={() => setSelectedFacilityId("all")}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  marginLeft: "8px",
                  padding: "0 4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}>
                ×
              </button>
            </span>
          )}

          {!isLoading && schedules.length > 0 && (
            <span className="badge bg-success p-2">
              <i className="fas fa-clock me-1"></i>
              {schedules.length} Schedules
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "10px" }}>Loading assessment schedules...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#D32F2F" }}>
          <Icon icon="material-symbols:error-outline" width="48" height="48" />
          <p style={{ marginTop: "10px" }}>Error: {error}</p>
          <button
            onClick={() => {
              const dateRange = getDateRange();
              const params = {
                from_date: dateRange.from_date,
                to_date: dateRange.to_date,
              };

              dispatch(getAssessmentSchedules(params));
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#8B2885",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {scheduleData.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <Icon icon="mdi:calendar-clock" width="48" height="48" />
              <p style={{ marginTop: "10px" }}>
                No assessment schedules found for {getDateRange().label}
                {selectedFacilityId !== "all" &&
                  ` at ${facilityOptions.find((f) => f.id === selectedFacilityId)?.name}`}
                .
              </p>
              <p style={{ fontSize: "14px", marginTop: "8px", color: "#999" }}>
                Try selecting a different date range or facility
              </p>
            </div>
          ) : (
            <div className="basic-data-table">
              <MUIDataTable
                title=""
                data={filteredData}
                columns={columns}
                options={options}
                className="overflow-hidden packageTable"
              />
            </div>
          )}
        </>
      )}

      <DynamicModal
        show={showViewModal}
        handleClose={handleCloseViewModal}
        title="Assessment Schedule Details"
        modalWidth="500px"
        content={
          <ViewScheduleModal
            schedule={selectedSchedule}
            onClose={handleCloseViewModal}
            onEdit={(schedule) => {
              setSelectedSchedule(schedule);
              setShowEditModal(true);
            }}
            onDelete={(schedule) => {
              handleDelete(schedule);
            }}
          />
        }
      />

      <DynamicModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        title={`Edit Schedule - ${selectedSchedule?.name || ""}`}
        modalWidth="500px"
        content={
          <EditAssessmentScheduleModal
            scheduleData={selectedSchedule}
            isEditMode={true}
            onClose={handleCloseEditModal}
            onSave={handleSaveSchedule}
            isSaving={isSaving || isUpdating}
            facilities={facilities}
            isLoadingFacilities={isLoadingFacilities}
          />
        }
      />

      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left,
              backgroundColor: "white",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "140px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleViewDetails(filteredData[dropdownOpen])}>
              <Icon
                icon="mdi:eye-outline"
                width="18"
                height="18"
                color="#2196F3"
              />
              <span>View Details</span>
            </div>
            {/* <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                borderTop: "1px solid #F0F0F0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleEdit(filteredData[dropdownOpen])}>
              <Icon icon="line-md:edit" width="18" height="18" color="#666" />
              <span>Edit</span>
            </div> */}
            {/* <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#D32F2F",
                borderTop: "1px solid #F0F0F0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleDelete(filteredData[dropdownOpen])}>
              <Icon
                icon="material-symbols:delete-outline"
                width="18"
                height="18"
                color="#D32F2F"
              />
              <span>{isDeleting ? "Deleting..." : "Delete"}</span>
            </div> */}
          </div>,
          document.body,
        )}
    </>
  );
});

// Set display name for debugging
AdminAssessmentSchedule.displayName = "AdminAssessmentSchedule";

export default AdminAssessmentSchedule;
